/* ============================================================
   <clic-3d-diagram> — interactive 3D product-features controller
   Paired with sections/section-3d-product-diagram.liquid

   - Demand-loads model-viewer + the GLB — never on page load. With
     data-autoload="true" the load starts when the section scrolls
     into view; otherwise on first interaction (poster click or
     feature tap).
   - Ports the reference viewer's animation-control pattern exactly
     (misc/3d-glasses/index.html): select clip -> play() then pause()
     to activate the action, a persistent rAF re-applies
     currentTime = value*duration every frame so the pose never
     reverts, and a tween drives value 0->1 (open) / 1->0 (close).
   - The persistent rAF is gated by IntersectionObserver + page
     visibility so it only runs while the section is on-screen.
   ============================================================ */
(function () {
  'use strict';
  if (customElements.get('clic-3d-diagram')) return;

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Load the vendored model-viewer module once, shared across sections.
  // A failed load clears the cached promise + script tag so a retry re-injects.
  function loadModelViewer(src) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (window.__clicMvPromise) return window.__clicMvPromise;
    window.__clicMvPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.type = 'module';
      s.src = src;
      s.onload = function () { customElements.whenDefined('model-viewer').then(resolve); };
      s.onerror = function () {
        window.__clicMvPromise = null;
        s.remove();
        reject(new Error('model-viewer failed to load'));
      };
      document.head.appendChild(s);
    });
    return window.__clicMvPromise;
  }

  function easeInOut(k) { return k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; }

  class Clic3dDiagram extends HTMLElement {
    connectedCallback() {
      if (this._init) {
        // re-connected (e.g. theme-editor section reorder): re-arm the gating
        if (this.io) this.io.observe(this);
        if (this._onVis) document.addEventListener('visibilitychange', this._onVis);
        if (this._applyZoomMode && this.mobileMq) {
          if (this.mobileMq.addEventListener) this.mobileMq.addEventListener('change', this._applyZoomMode);
          else if (this.mobileMq.addListener) this.mobileMq.addListener(this._applyZoomMode);
        }
        if (this.ready && this.onScreen) { this.startLoop(); this.maybeAutoRotate(); }
        return;
      }
      this._init = true;

      this.viewer = this.querySelector('model-viewer');
      this.poster = this.querySelector('[data-clic-3d-reveal]');
      this.loader = this.querySelector('.clic-3d__loader');
      this.errorNote = this.querySelector('[data-clic-3d-error]');
      this.hint = this.querySelector('[data-clic-3d-hint]');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('.clic-3d__chip'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('.clic-3d__panel'));
      this.hotspots = Array.prototype.slice.call(this.querySelectorAll('.clic-3d__hotspot'));
      this.section = this.closest('.clic-3d');
      if (!this.viewer) return;

      this.modelSrc = this.dataset.modelSrc || '';
      this.mvSrc = this.dataset.mvSrc || '';
      this.autoRotate = this.dataset.autoRotate === 'true';
      // storefront autoload + theme-editor preview share the same deferred
      // reveal: wait for scroll-into-view so the GLB never loads off-screen
      this._autoReveal = this.dataset.autoload === 'true' || (window.Shopify && window.Shopify.designMode);

      // per-viewport zoom permissions + the mobile camera pull-back for clips
      // (900px matches the section CSS's desktop breakpoint)
      this.zoomDesktop = this.dataset.enableZoom !== 'false';
      this.zoomMobile = this.dataset.enableZoomMobile !== 'false';
      this.mobileOrbitZoom = parseInt(this.dataset.mobileOrbitZoom, 10) || 0;
      this.mobileMq = window.matchMedia('(max-width: 899px)');

      // per-feature clip + preset-view lookups (index -> clip name or 'none' / orbit or '')
      var self = this;
      this.clips = this.chips.map(function (c, i) {
        var h = self.hotspots.filter(function (h) { return +h.dataset.feature === i; })[0];
        return h ? (h.dataset.clip || 'none') : 'none';
      });
      this.orbits = this.chips.map(function (c, i) {
        var h = self.hotspots.filter(function (h) { return +h.dataset.feature === i; })[0];
        return h ? (h.dataset.orbit || '') : '';
      });

      // animation state (mirrors the reference viewer)
      this.ready = false;
      this.revealing = false;
      this.busy = false;
      this.queued = null;
      this.pending = null;     // feature requested before the model was ready
      this.active = null;      // feature whose clip is currently held open
      this.curClip = null;     // animationName currently applied
      this.value = 0;          // held position 0..1 of curClip
      this.tween = null;
      this.rafId = null;
      this.onScreen = false;
      this._loop = this.loop.bind(this);

      // events
      if (this.poster) this.poster.addEventListener('click', function () { self.reveal(); });
      this.chips.forEach(function (c) {
        c.addEventListener('click', function () { self.select(+c.dataset.feature); });
        c.addEventListener('keydown', function (e) { self.onChipKey(e, c); });
      });
      this.hotspots.forEach(function (h) {
        h.addEventListener('click', function () { self.select(+h.dataset.feature); });
      });

      // apply the viewport's zoom permission now and on viewport changes
      this._applyZoomMode = function () {
        var allow = self.mobileMq.matches ? self.zoomMobile : self.zoomDesktop;
        if (allow) self.viewer.removeAttribute('disable-zoom');
        else self.viewer.setAttribute('disable-zoom', '');
      };
      this._applyZoomMode();
      if (this.mobileMq.addEventListener) this.mobileMq.addEventListener('change', this._applyZoomMode);
      else if (this.mobileMq.addListener) this.mobileMq.addListener(this._applyZoomMode);

      // gate the persistent rAF to on-screen + visible
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            self.onScreen = e.isIntersecting;
            if (e.isIntersecting) {
              if (self.ready) { self.startLoop(); self.maybeAutoRotate(); }
              // autoload / theme editor: reveal only once the section scrolls
              // into view (an unconditional reveal would force ~2.8MB per load)
              if (self._autoReveal && !self.ready) { self._autoReveal = false; self.reveal(); }
            } else {
              self.stopLoop();
              if (self.viewer) self.viewer.removeAttribute('auto-rotate');
            }
          });
        }, { rootMargin: '200px 0px' });
        this.io.observe(this);
      } else {
        this.onScreen = true;
        if (this._autoReveal) { this._autoReveal = false; requestAnimationFrame(function () { self.reveal(); }); }
      }
      this._onVis = function () {
        if (document.hidden) self.stopLoop();
        else if (self.ready && self.onScreen) self.startLoop();
      };
      document.addEventListener('visibilitychange', this._onVis);

      // Theme editor: follow block selection (the auto-preview reveal is
      // handled by the shared _autoReveal path above)
      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = function (e) {
          var id = e.detail && e.detail.blockId;
          if (!id) return;
          var chip = self.chips.filter(function (c) {
            return c.dataset.blockId && id.indexOf(c.dataset.blockId) !== -1;
          })[0];
          if (chip) self.select(+chip.dataset.feature);
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);
      }

      this.paintSelection(0); // sync roving tabindex + hotspot state with the initial markup
    }

    /* ---- reveal / load ---- */
    reveal() {
      if (this.revealing || this.ready || !this.modelSrc) return;
      this.revealing = true;
      if (this.section) this.section.classList.add('is-revealing');
      if (this.loader) this.loader.hidden = false;
      if (this.errorNote) this.errorNote.hidden = true;
      var self = this;
      loadModelViewer(this.mvSrc).then(function () {
        self.viewer.addEventListener('load', function () { self.onReady(); }, { once: true });
        self.viewer.addEventListener('error', function () { self.onError(); }, { once: true });
        self.maybeAutoRotate();
        self.viewer.src = self.modelSrc; // empty until now -> demand-load
      }).catch(function () { self.onError(); });
    }

    onReady() {
      this.ready = true;
      this.revealing = false;
      if (this.loader) this.loader.hidden = true;
      if (this.section) { this.section.classList.remove('is-revealing'); this.section.classList.add('is-ready'); }
      // drop the (now invisible) poster button from the tab order once its fade ends
      if (this.poster) {
        var poster = this.poster;
        setTimeout(function () { poster.hidden = true; }, 450);
      }
      if (this.hint) {
        this.hint.hidden = false;
        var hint = this.hint;
        setTimeout(function () { if (hint) hint.hidden = true; }, 5000);
      }
      // keep only clips that actually exist in the GLB
      var avail = this.viewer.availableAnimations || [];
      this.clips = this.clips.map(function (c) { return (c === 'none' || avail.indexOf(c) !== -1) ? c : 'none'; });
      this.startLoop();
      this.maybeAutoRotate();
      if (this.pending != null) { var p = this.pending; this.pending = null; this.select(p); }
    }

    onError() {
      this.revealing = false;
      this.pending = null;
      if (this.loader) this.loader.hidden = true;
      // drop the autoload styling so the poster (with its CTA) returns as a
      // clickable retry — the error copy says "tap the image to try again"
      if (this.section) this.section.classList.remove('is-revealing', 'is-autoloading');
      // reset the src so a retry assignment is a real change model-viewer acts on
      if (this.viewer) this.viewer.removeAttribute('src');
      if (this.poster) this.poster.hidden = false;
      if (this.errorNote) this.errorNote.hidden = false;
    }

    maybeAutoRotate() {
      if (!this.viewer) return;
      if (this.autoRotate && !prefersReduced && this.active == null && this.onScreen) {
        this.viewer.setAttribute('auto-rotate', '');
      } else {
        this.viewer.removeAttribute('auto-rotate');
      }
    }

    /* ---- feature selection ---- */
    select(i) {
      if (isNaN(i)) return;
      this.paintSelection(i);
      if (!this.ready) { this.pending = i; this.reveal(); return; }
      if (this.busy) { this.queued = i; return; }
      var self = this;
      var clip = this.clips[i];
      this.viewer.removeAttribute('auto-rotate'); // stop spinning to show the feature
      // glide to the feature's preset view so its animation reads clearly from
      // wherever the user left the camera (model-viewer interpolates the change).
      // auto-rotate spins the turntable, not the camera, so fold the frozen
      // turntable angle into theta or the preset lands on the wrong face
      // (verified: theta = preset + turntableRotation reproduces the tt=0 view).
      if (this.orbits[i]) {
        var orbit = this.orbits[i];
        // on mobile, pull the camera back from the auto framing while a clip
        // plays so animated parts (e.g. the 180° hinges opening flat) don't
        // leave the narrow stage — model-viewer resolves a % radius relative
        // to the auto distance (its own default radius is 105%)
        if (this.mobileMq.matches && this.mobileOrbitZoom > 105 && clip !== 'none') {
          orbit = orbit.replace(/\bauto\s*$/, this.mobileOrbitZoom + '%');
          // the default max-camera-orbit radius clamps at the auto framing
          // distance — widen it or the pull-back silently clamps back
          this.viewer.maxCameraOrbit = 'auto auto ' + this.mobileOrbitZoom + '%';
        }
        var tt = this.viewer.turntableRotation || 0; // radians
        if (tt) {
          var m = orbit.match(/^(-?[\d.]+)deg(\s+.*)$/);
          if (m) orbit = (parseFloat(m[1]) + tt * 180 / Math.PI) + 'deg' + m[2];
        }
        this.viewer.cameraOrbit = orbit;
        if (prefersReduced && this.viewer.jumpCameraToGoal) this.viewer.jumpCameraToGoal();
      }
      this.busy = true;
      (function run() {
        var chain;
        if (self.active === i) {
          // re-selecting the open feature toggles its clip open/closed
          chain = clip !== 'none' ? self.animateTo(clip, self.value > 0.5 ? 0 : 1) : Promise.resolve();
        } else {
          var close = (self.active != null && self.clips[self.active] !== 'none')
            ? self.animateTo(self.clips[self.active], 0) : Promise.resolve();
          chain = close.then(function () {
            self.active = i;
            return clip !== 'none' ? self.animateTo(clip, 1) : Promise.resolve();
          });
        }
        chain.then(function () {
          self.busy = false;
          if (self.queued != null) { var q = self.queued; self.queued = null; self.select(q); }
        });
      })();
    }

    paintSelection(i) {
      this.chips.forEach(function (c, idx) {
        var on = idx === i;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-selected', on ? 'true' : 'false');
        c.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach(function (p, idx) {
        var on = idx === i;
        p.classList.toggle('is-active', on);
        if (on) p.removeAttribute('hidden'); else p.setAttribute('hidden', '');
      });
      this.hotspots.forEach(function (h) {
        var on = +h.dataset.feature === i;
        h.classList.toggle('is-active', on);
        h.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    onChipKey(e, chip) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      var idx = this.chips.indexOf(chip);
      var next = e.key === 'ArrowRight' ? (idx + 1) % this.chips.length : (idx - 1 + this.chips.length) % this.chips.length;
      this.chips[next].focus();
      this.select(next);
    }

    /* ---- animation control (ported from misc/3d-glasses/index.html) ---- */
    ensureClip(clip) {
      if (this.curClip === clip) return;
      this.viewer.animationName = clip;
      this.viewer.play();
      this.viewer.pause(); // activate the action; WE drive currentTime
      this.curClip = clip;
    }

    applyPose() {
      if (!this.curClip) return;
      var dur = this.viewer.duration || 0.8;
      this.viewer.currentTime = Math.min(this.value, 0.999) * dur; // cap avoids end-wrap
    }

    loop(now) {
      if (this.tween) {
        var k = Math.min(1, (now - this.tween.start) / this.tween.ms);
        this.value = this.tween.from + (this.tween.to - this.tween.from) * easeInOut(k);
        if (k >= 1) { this.value = this.tween.to; var cb = this.tween.cb; this.tween = null; if (cb) cb(); }
      }
      this.applyPose();
      // Only keep the rAF alive while animating or while a clip is held open.
      // At rest (currentTime 0 == the model's default pose) stop, to save battery.
      if (this.tween || (this.curClip && this.value > 0.001)) {
        this.rafId = requestAnimationFrame(this._loop);
      } else {
        this.rafId = null;
      }
    }

    startLoop() { if (this.rafId == null && this.ready) this.rafId = requestAnimationFrame(this._loop); }
    stopLoop() { if (this.rafId != null) { cancelAnimationFrame(this.rafId); this.rafId = null; } }

    animateTo(clip, to) {
      var self = this;
      return new Promise(function (resolve) {
        self.ensureClip(clip);
        if (prefersReduced) {
          // jump straight to the pose, but the hold-rAF must still run:
          // a scrubbed currentTime reverts unless re-applied every frame
          self.value = to;
          self.applyPose();
          self.startLoop();
          resolve();
          return;
        }
        var ms = Math.max(450, (self.viewer.duration || 0.8) * 1000);
        self.tween = { from: self.value, to: to, start: performance.now(), ms: ms, cb: resolve };
        self.startLoop();
      });
    }

    disconnectedCallback() {
      this.stopLoop();
      if (this.io) this.io.disconnect();
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._applyZoomMode && this.mobileMq) {
        if (this.mobileMq.removeEventListener) this.mobileMq.removeEventListener('change', this._applyZoomMode);
        else if (this.mobileMq.removeListener) this.mobileMq.removeListener(this._applyZoomMode);
      }
    }
  }

  customElements.define('clic-3d-diagram', Clic3dDiagram);
})();
